import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { TIDS } from "@/constants/testIds";
import { Coins, Check, Sparkles, TrendingUp } from "lucide-react";

const KIND_LABEL = { purchase: "Purchase", spend: "Used", grant: "Bonus", refund: "Refund" };
const KIND_COLOR = {
  purchase: "text-[#1E8E3E]", grant: "text-[#0B5CFF]", refund: "text-[#0B5CFF]", spend: "text-[#B00020]",
};

export default function Credits() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = async () => {
    const { data } = await api.get("/credits/wallet");
    setData(data);
  };
  useEffect(() => { load(); }, []);

  const buy = async (pack) => {
    setBusy(pack);
    try {
      const { data } = await api.post("/credits/purchase", { pack });
      toast.success(`+${data.credited} credits added. Balance ${data.balance}.`);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setBusy(null); }
  };

  if (!data) return <div className="text-[#4A4D57]">Loading…</div>;

  return (
    <div className="space-y-8 vp-fade-up max-w-4xl">
      <div>
        <div className="vp-label">Credits</div>
        <h1 className="font-display text-3xl font-bold mt-1">AI credits · fuel for your generations</h1>
        <p className="text-[#4A4D57] mt-2 text-sm max-w-xl">
          Spend credits on AI landing page generation, translations, and future magic features. Credits never expire.
        </p>
      </div>

      <div className="grid sm:grid-cols-3 gap-3">
        <div className="vp-kpi flex items-center gap-3">
          <Coins className="w-6 h-6 text-[#F5A623]" strokeWidth={1.75} />
          <div>
            <div className="vp-label">Current balance</div>
            <div className="font-display text-3xl font-bold mt-1" data-testid={TIDS.creditBalance}>{data.balance}</div>
          </div>
        </div>
        <div className="vp-kpi">
          <div className="vp-label">Lifetime earned</div>
          <div className="font-display text-2xl font-bold mt-1">{data.lifetime_earned}</div>
        </div>
        <div className="vp-kpi">
          <div className="vp-label">Lifetime spent</div>
          <div className="font-display text-2xl font-bold mt-1">{data.lifetime_spent}</div>
        </div>
      </div>

      <div>
        <div className="vp-label mb-4">Buy credits</div>
        <div className="grid md:grid-cols-3 gap-4">
          {data.packs.map((p) => {
            const featured = p.pack === "growth";
            return (
              <div key={p.pack} data-testid={TIDS.creditPackCard + "-" + p.pack}
                className={"vp-glass p-6 flex flex-col relative " + (featured ? "border-[#0B5CFF] shadow-[0_18px_44px_rgba(11,92,255,0.18)]" : "")}>
                {featured && <div className="absolute -top-3 left-1/2 -translate-x-1/2 vp-chip vp-chip-accent">Popular</div>}
                <div className="font-display font-bold text-xl">{p.label}</div>
                <div className="mt-3 flex items-baseline gap-2">
                  <span className="font-display font-black text-4xl tracking-tight">{p.credits}</span>
                  <span className="text-[#747680] text-sm">credits</span>
                </div>
                <div className="text-sm text-[#4A4D57] mt-2">₹{p.inr.toLocaleString("en-IN")}</div>
                {p.bonus && <div className="text-xs text-[#1E8E3E] font-semibold mt-1 inline-flex items-center gap-1"><TrendingUp className="w-3.5 h-3.5" /> {p.bonus}</div>}
                <div className="text-xs text-[#747680] mt-4">
                  ≈ {Math.floor(p.credits / data.ai_landing_cost)} AI landing generations
                </div>
                <button onClick={() => buy(p.pack)} disabled={busy === p.pack}
                  data-testid={TIDS.creditPurchaseBtn + "-" + p.pack}
                  className={"mt-5 " + (featured ? "vp-btn-primary" : "vp-btn-ghost")}>
                  {busy === p.pack ? "Processing…" : `Buy ${p.label}`}
                </button>
              </div>
            );
          })}
        </div>
        <div className="text-xs text-[#747680] mt-3">
          MVP note: purchases are instant (MOCK). Razorpay live checkout wires in as soon as you provide keys.
        </div>
      </div>

      <div>
        <div className="vp-label mb-4">Recent activity</div>
        <div className="vp-card overflow-hidden">
          {data.transactions.length === 0 ? (
            <div className="p-6 text-[#4A4D57] text-sm text-center">No transactions yet.</div>
          ) : (
            <div className="divide-y divide-[#E4E6EC]">
              {data.transactions.map((t) => (
                <div key={t.id} className="p-4 flex items-center justify-between text-sm">
                  <div>
                    <div className="font-medium text-[#0B0D17]">{t.note}</div>
                    <div className="text-xs text-[#747680] mt-0.5">{new Date(t.created_at).toLocaleString("en-IN")}</div>
                  </div>
                  <div className={"font-mono font-semibold " + (KIND_COLOR[t.kind] || "")}>
                    {t.kind === "spend" ? "−" : "+"}{t.amount}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
